import React from "react";
import PropTypes from "prop-types";
import {
    Avatar,
    Box,
    Button,
    Fade,
    IconButton,
    Popper,
    Typography,
    useMediaQuery,
} from "@mui/material";
import { Close, PersonOutlineRounded } from "@mui/icons-material";
import { alpha } from "@mui/material/styles";
import { colors } from "../../theme/colors";
import StatusDot from "../common/StatusDot";
import { getEloTier, getEloTierProgress } from "../common/eloTierUtils";
import {
    MembershipBadge,
    ProfileRankBadge,
    resolveMembershipDisplay,
} from "../profile/profileIdentityBadges";
import useLobbyPlayerPreview from "../lobby/useLobbyPlayerPreview";
import { MOBILE_NAV_MEDIA } from "./layoutConstants";
import { useScrollLock } from "../common/lenisContext";
import BackdropOverlay from "../common/BackdropOverlay.jsx";

const CARD_WIDTH = "286px";
const CARD_PAD = "1.15rem";
const AVATAR_SIZE = "3.35rem";
const MOBILE_CARD_PAD = "1.5rem";
const MOBILE_HEADER_HEIGHT = "5rem";
const MOBILE_AVATAR_SIZE = "4rem";
const MOBILE_AVATAR_OVERLAP = "-2rem";
const EMPTY_SLOT_FONT = "'JetBrains Mono', monospace";
const ACTION_CLIP = "polygon(6px 0, 100% 0, calc(100% - 6px) 100%, 0 100%)";

const PAPER_SX = {
    width: CARD_WIDTH,
    maxWidth: "calc(100vw - 1.5rem)",
    overflow: "visible",
    position: "relative",
    isolation: "isolate",
    boxSizing: "border-box",
    transformOrigin: "top right",
    borderRadius: "8px",
    border: "1px solid rgba(255,255,255,0.075)",
    background:
        "linear-gradient(165deg, rgba(18,19,23,0.995) 0%, rgba(11,12,15,0.997) 52%, rgba(8,9,11,1) 100%)",
    boxShadow:
        "inset 0 1px 0 rgba(255,255,255,0.055), inset 1px 0 0 rgba(255,255,255,0.018), inset 0 -1px 0 rgba(0,0,0,0.72), 0 24px 64px rgba(0,0,0,0.62), 0 8px 20px rgba(0,0,0,0.28)",
    backdropFilter: "blur(22px)",
    WebkitBackdropFilter: "blur(22px)",
    "&::before": {
        content: '""',
        position: "absolute",
        inset: 0,
        zIndex: -1,
        pointerEvents: "none",
        borderRadius: "inherit",
        background:
            "radial-gradient(ellipse 85% 42% at 22% -6%, rgba(232,163,61,0.075), transparent 66%), radial-gradient(ellipse 70% 55% at 100% 100%, rgba(40,48,66,0.11), transparent 70%)",
    },
    "&::after": {
        content: '""',
        position: "absolute",
        top: 0,
        left: "14%",
        right: "14%",
        height: "1px",
        zIndex: 4,
        pointerEvents: "none",
        background:
            "linear-gradient(90deg, transparent, rgba(232,163,61,.38), transparent)",
    },
};

const MOBILE_PAPER_SX = {
    ...PAPER_SX,
    width: "100%",
    maxWidth: "100%",
    maxHeight: "calc(100dvh - 2.5rem)",
    overflowY: "auto",
    transformOrigin: "top center",
};

const CLOSE_BUTTON_SX = {
    position: "absolute",
    top: "0.8rem",
    right: "0.8rem",
    zIndex: 5,
    width: "1.9rem",
    height: "1.9rem",
    borderRadius: "0.55rem",
    color: colors.text.secondary,
    backgroundColor: "rgba(255, 255, 255, 0.04)",
    "&:hover": {
        backgroundColor: "rgba(255, 255, 255, 0.08)",
    },
};

const HEADER_SX = {
    position: "relative",
    height: "4.1rem",
    boxSizing: "border-box",
    overflow: "hidden",
    background:
        "radial-gradient(ellipse 220px 90px at 18% 0%, rgba(232,163,61,.1), transparent 72%), linear-gradient(135deg, rgba(27,24,20,.82) 0%, rgba(14,15,18,.97) 52%, rgba(9,10,12,.99) 100%)",
    "&::before": {
        content: '""',
        position: "absolute",
        inset: 0,
        pointerEvents: "none",
        background: `
            repeating-linear-gradient(
                118deg,
                transparent 0 10px,
                rgba(255, 236, 200, 0.028) 10px 11px
            )
        `,
        maskImage:
            "linear-gradient(180deg, rgba(0,0,0,0.9) 0%, rgba(0,0,0,0.45) 62%, transparent 100%)",
        WebkitMaskImage:
            "linear-gradient(180deg, rgba(0,0,0,0.9) 0%, rgba(0,0,0,0.45) 62%, transparent 100%)",
    },
};

const AVATAR_RING_SX = {
    position: "relative",
    zIndex: 2,
    width: AVATAR_SIZE,
    height: AVATAR_SIZE,
    flex: `0 0 ${AVATAR_SIZE}`,
    mt: "-2rem",
    p: "2px",
    boxSizing: "border-box",
    borderRadius: "50%",
    background:
        "linear-gradient(145deg, rgba(245,190,91,.88), rgba(171,108,28,.46) 48%, rgba(255,255,255,.10))",
    boxShadow: "0 6px 16px rgba(0,0,0,.42), 0 0 0 1px rgba(232,163,61,.08)",
};

const AVATAR_SX = {
    width: "100%",
    height: "100%",
    borderRadius: "50%",
    border: "1px solid " + colors.background.base,
    boxSizing: "border-box",
    backgroundColor: "rgba(255,255,255,0.055)",
};

const VIEW_PROFILE_SX = {
    width: "100%",
    minHeight: 32,
    px: "0.75rem",
    justifyContent: "center",
    borderRadius: "8px",
    clipPath: ACTION_CLIP,
    textTransform: "none",
    whiteSpace: "nowrap",
    fontSize: "0.78rem",
    fontWeight: 700,
    letterSpacing: "0.03em",
    color: colors.text.contrast,
    border: "1px solid rgba(246,199,107,0.4)",
    background: colors.gradient.accent,
    boxShadow: "0 6px 16px rgba(232,163,61,0.16)",
    transition:
        "background-color 160ms ease, border-color 160ms ease, color 160ms ease, box-shadow 160ms ease, filter 160ms ease",
    "& .MuiButton-startIcon": {
        mr: "0.35rem",
        color: "inherit",
    },
    "&:hover": {
        filter: "brightness(1.04)",
        boxShadow: "0 8px 18px rgba(232,163,61,0.22)",
    },
};

const REMOVE_ACTION_SX = {
    minWidth: 0,
    width: "100%",
    minHeight: "2.15rem",
    px: "0.75rem",
    justifyContent: "center",
    borderRadius: "7px",
    textTransform: "none",
    letterSpacing: "0.005em",
    fontSize: "0.72rem",
    fontWeight: 600,
    color: alpha(colors.text.secondary, 0.8),
    border: "1px solid transparent",
    background: "transparent",
    boxShadow: "none",
    transition: "color 160ms ease, background 160ms ease",
    "&:hover": {
        color: colors.state.danger.main,
        background: alpha(colors.state.danger.hover, 0.14),
        backgroundColor: alpha(colors.state.danger.hover, 0.14),
        borderColor: "transparent",
        boxShadow: "none",
    },
    "&:active": {
        background: alpha(colors.state.danger.hover, 0.18),
        backgroundColor: alpha(colors.state.danger.hover, 0.18),
    },
};

const ACTIONS_FOOTER_SX = {
    mt: "1.1rem",
    pt: "0.5rem",
    borderTop: "1px solid rgba(255,255,255,0.065)",
};

function resolveStatus(isOnline, playerStatus) {
    if (!isOnline) return { label: "Offline", live: false };
    if (playerStatus === "inMatch") return { label: "In match", live: true };
    if (playerStatus === "inMatchMaking") {
        return { label: "In queue", live: true };
    }
    return { label: "Online", live: true };
}

function RankEloStrip({ elo }) {
    const tier = getEloTier(elo);
    const { nextTier, isMax } = getEloTierProgress(elo);
    const hasElo = elo != null && Number.isFinite(Number(elo));
    const accent = tier.border;

    return (
        <Box
            sx={{
                position: "relative",
                mt: "0.95rem",
                width: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: "0.75rem",
                overflow: "visible",
                px: "0.85rem",
                py: "0.72rem",
                borderRadius: 0,
                border: "none",
                background: `
                    linear-gradient(
                        90deg,
                        ${alpha(accent, 0.14)} 0%,
                        ${alpha(accent, 0.045)} 42%,
                        ${alpha(accent, 0.018)} 100%
                    )
                `,
                "&::before": {
                    content: '""',
                    position: "absolute",
                    left: "5px",
                    top: "20%",
                    bottom: "20%",
                    width: "2px",
                    borderRadius: "999px",
                    background: `linear-gradient(180deg, transparent, ${accent}, transparent)`,
                    boxShadow: `0 0 9px ${alpha(accent, 0.32)}`,
                    pointerEvents: "none",
                },
            }}
        >
            <Box sx={{ minWidth: 0 }}>
                <Typography
                    component="p"
                    sx={{
                        m: 0,
                        fontSize: "0.72rem",
                        fontWeight: 700,
                        letterSpacing: "0.04em",
                        lineHeight: 1.15,
                        color: alpha(tier.text, 0.92),
                    }}
                >
                    {tier.label}
                </Typography>
                <Box
                    sx={{
                        mt: "0.28rem",
                        display: "flex",
                        alignItems: "baseline",
                        gap: "0.32rem",
                    }}
                >
                    <Typography
                        component="span"
                        sx={{
                            fontFamily: EMPTY_SLOT_FONT,
                            fontSize: "0.98rem",
                            fontWeight: 600,
                            letterSpacing: "0.02em",
                            lineHeight: 1,
                            color: colors.text.primary,
                            fontVariantNumeric: "tabular-nums",
                        }}
                    >
                        {hasElo ? Number(elo).toLocaleString("en-US") : "—"}
                    </Typography>
                    <Typography
                        component="span"
                        sx={{
                            fontSize: "0.58rem",
                            fontWeight: 650,
                            letterSpacing: "0.1em",
                            textTransform: "uppercase",
                            color: alpha(colors.text.secondary, 0.52),
                            lineHeight: 1,
                        }}
                    >
                        ELO
                    </Typography>
                </Box>
            </Box>

            <Box sx={{ flex: "0 0 auto", textAlign: "right" }}>
                {isMax ? (
                    <Typography
                        component="p"
                        sx={{
                            m: 0,
                            fontFamily: "'Orbitron', sans-serif",
                            fontSize: "0.62rem",
                            fontWeight: 800,
                            letterSpacing: "0.14em",
                            textTransform: "uppercase",
                            lineHeight: 1.15,
                            background:
                                "linear-gradient(180deg, #FFF6D2 0%, #F6D67A 32%, #E8A33D 68%, #B8741F 100%)",
                            backgroundClip: "text",
                            WebkitBackgroundClip: "text",
                            WebkitTextFillColor: "transparent",
                            filter: `
                                drop-shadow(0 0 8px rgba(232,163,61,0.42))
                                drop-shadow(0 1px 0 rgba(80,42,0,0.55))
                            `,
                        }}
                    >
                        Max Rank
                    </Typography>
                ) : (
                    <>
                        <Typography
                            component="p"
                            sx={{
                                m: 0,
                                fontSize: "0.52rem",
                                fontWeight: 650,
                                letterSpacing: "0.1em",
                                textTransform: "uppercase",
                                color: alpha(colors.text.secondary, 0.52),
                                lineHeight: 1,
                            }}
                        >
                            Next rank
                        </Typography>
                        <Typography
                            component="p"
                            sx={{
                                m: 0,
                                mt: "0.28rem",
                                fontSize: "0.72rem",
                                fontWeight: 700,
                                letterSpacing: "0.02em",
                                lineHeight: 1,
                                color: colors.text.primary,
                            }}
                        >
                            {nextTier?.label || "—"}
                        </Typography>
                    </>
                )}
            </Box>
        </Box>
    );
}

RankEloStrip.propTypes = {
    elo: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
};

export default function FriendRowPreviewCard({
    open,
    anchorEl,
    friendData,
    avatarSrc,
    isOnline,
    playerStatus,
    onProfile,
    onRemove,
    onClose,
}) {
    const isMobile = useMediaQuery(MOBILE_NAV_MEDIA);
    useScrollLock(Boolean(isMobile && open));
    const preview = useLobbyPlayerPreview(friendData, open);
    const nickname = friendData?.nickname || "Player";
    const status = resolveStatus(isOnline, playerStatus);
    const flagImageUrl = preview.flagImageUrl || friendData?.flagImageUrl;
    const membershipDisplay = resolveMembershipDisplay(
        {
            ...friendData,
            membership: preview.membership || friendData?.membership,
            membershipTier:
                preview.membershipTier ||
                friendData?.membershipTier ||
                friendData?.membership?.tier,
        },
        { compact: true },
    );

    const cardPad = isMobile ? MOBILE_CARD_PAD : CARD_PAD;

    const card = (
                    <Box
                        data-friend-row-menu
                        role="dialog"
                        aria-label={nickname + " friend menu"}
                        sx={isMobile ? MOBILE_PAPER_SX : PAPER_SX}
                        onClick={(event) => event.stopPropagation()}
                    >
                        {isMobile ? (
                            <IconButton
                                onClick={onClose}
                                aria-label="Close dialog"
                                sx={CLOSE_BUTTON_SX}
                            >
                                <Close sx={{ fontSize: "0.95rem" }} />
                            </IconButton>
                        ) : null}
                        <Box
                            sx={{
                                overflow: "hidden",
                                borderRadius: "inherit",
                            }}
                        >
                            <Box
                                sx={{
                                    ...HEADER_SX,
                                    ...(isMobile
                                        ? { height: MOBILE_HEADER_HEIGHT }
                                        : {}),
                                }}
                            >
                                {!isMobile && preview.globalRank != null ? (
                                    <Box
                                        sx={{
                                            position: "absolute",
                                            top: "0.65rem",
                                            right: CARD_PAD,
                                            zIndex: 2,
                                        }}
                                    >
                                        <ProfileRankBadge
                                            rank={preview.globalRank}
                                            size="compact"
                                        />
                                    </Box>
                                ) : null}
                            </Box>

                            <Box
                                sx={{
                                    px: cardPad,
                                    pt: 0,
                                    pb: cardPad,
                                }}
                            >
                                <Box
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: isMobile
                                            ? "space-between"
                                            : "flex-start",
                                        width: isMobile ? "100%" : "auto",
                                        ...(isMobile
                                            ? { mt: MOBILE_AVATAR_OVERLAP }
                                            : {}),
                                    }}
                                >
                                <Box
                                    sx={{
                                        ...AVATAR_RING_SX,
                                        ...(isMobile
                                            ? {
                                                  mt: 0,
                                                  width: MOBILE_AVATAR_SIZE,
                                                  height: MOBILE_AVATAR_SIZE,
                                                  flex: `0 0 ${MOBILE_AVATAR_SIZE}`,
                                              }
                                            : {}),
                                    }}
                                >
                                    <Avatar
                                        src={avatarSrc || undefined}
                                        alt=""
                                        sx={AVATAR_SX}
                                    />
                                    {flagImageUrl ? (
                                        <Box
                                            component="img"
                                            src={flagImageUrl}
                                            alt=""
                                            aria-hidden
                                            sx={{
                                                position: "absolute",
                                                right: "-1px",
                                                bottom: "-1px",
                                                width: "0.92rem",
                                                height: "0.92rem",
                                                borderRadius: "50%",
                                                border: "2px solid rgba(11,12,15,1)",
                                                objectFit: "cover",
                                            }}
                                        />
                                    ) : null}
                                </Box>
                                {isMobile && preview.globalRank != null ? (
                                    <ProfileRankBadge
                                        rank={preview.globalRank}
                                        size="compact"
                                    />
                                ) : null}
                                </Box>

                                <Box
                                    sx={{
                                        mt: "0.85rem",
                                        display: "flex",
                                        alignItems: "center",
                                        gap: "0.5rem",
                                        minWidth: 0,
                                    }}
                                >
                                    <Typography
                                        component="p"
                                        title={nickname}
                                        sx={{
                                            m: 0,
                                            minWidth: 0,
                                            color: colors.text.primary,
                                            fontSize: "0.95rem",
                                            fontWeight: 700,
                                            letterSpacing: "0.005em",
                                            lineHeight: 1.15,
                                            overflow: "hidden",
                                            textOverflow: "ellipsis",
                                            whiteSpace: "nowrap",
                                        }}
                                    >
                                        {nickname}
                                    </Typography>
                                    {membershipDisplay ? (
                                        <MembershipBadge
                                            label={membershipDisplay.label}
                                            size="compact"
                                        />
                                    ) : null}
                                </Box>

                                <Box
                                    sx={{
                                        mt: "0.45rem",
                                        display: "flex",
                                        alignItems: "center",
                                        gap: "0.38rem",
                                    }}
                                >
                                    <StatusDot
                                        online={status.live}
                                        sx={{ fontSize: "0.44rem" }}
                                    />
                                    <Typography
                                        component="span"
                                        sx={{
                                            color: status.live
                                                ? alpha(
                                                      colors.state.success.main,
                                                      0.86,
                                                  )
                                                : alpha(
                                                      colors.text.secondary,
                                                      0.58,
                                                  ),
                                            fontSize: "0.62rem",
                                            fontWeight: 650,
                                            letterSpacing: "0.06em",
                                            lineHeight: 1,
                                        }}
                                    >
                                        {status.label}
                                    </Typography>
                                </Box>

                                <RankEloStrip
                                    elo={preview.elo ?? friendData?.elo}
                                />

                                <Box sx={{ mt: "1.2rem" }}>
                                    <Button
                                        type="button"
                                        disableElevation
                                        disableRipple
                                        onClick={onProfile}
                                        startIcon={
                                            <PersonOutlineRounded
                                                sx={{
                                                    fontSize:
                                                        "0.95rem !important",
                                                }}
                                            />
                                        }
                                        sx={VIEW_PROFILE_SX}
                                    >
                                        View profile
                                    </Button>
                                </Box>

                                {typeof onRemove === "function" ? (
                                <Box sx={ACTIONS_FOOTER_SX}>
                                    <Button
                                        type="button"
                                        disableElevation
                                        disableRipple
                                        onClick={onRemove}
                                        sx={REMOVE_ACTION_SX}
                                    >
                                        Remove
                                    </Button>
                                </Box>
                                ) : null}
                            </Box>
                        </Box>
                    </Box>
    );

    if (isMobile) {
        return (
            <BackdropOverlay
                open={open}
                onClick={onClose}
                contentSx={{ width: "100%", maxWidth: "100%" }}
            >
                {card}
            </BackdropOverlay>
        );
    }

    return (
        <Popper
            open={open}
            anchorEl={anchorEl}
            placement="left-start"
            modifiers={[
                { name: "offset", options: { offset: [0, 12] } },
                {
                    name: "preventOverflow",
                    options: { padding: 12, altAxis: true, tether: true },
                },
                {
                    name: "flip",
                    options: {
                        fallbackPlacements: [
                            "left-end",
                            "right-start",
                            "bottom-start",
                        ],
                        padding: 12,
                    },
                },
            ]}
            sx={{ zIndex: 1500 }}
            transition
        >
            {({ TransitionProps }) => (
                <Fade {...TransitionProps} timeout={180}>
                    {card}
                </Fade>
            )}
        </Popper>
    );
}

FriendRowPreviewCard.propTypes = {
    open: PropTypes.bool,
    anchorEl: PropTypes.object,
    friendData: PropTypes.object,
    avatarSrc: PropTypes.string,
    isOnline: PropTypes.bool,
    playerStatus: PropTypes.string,
    onProfile: PropTypes.func,
    onRemove: PropTypes.func,
    onClose: PropTypes.func,
};
